import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";
import { updateChapterOrder } from "../../State/Chapter/Action";
import { motion } from "framer-motion";
import {
  GripVertical,
  BookOpen,
  PlayCircle,
  FileText,
  HelpCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";

const ReOrderChapter = ({ chapters, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { updateOrderLoading, updateOrderError } = useSelector((store) => store.chapter);
  const [chapterList, setChapterList] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    // Initialize chapter list with orderIndex
    const sortedChapters = [...chapters].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
    setChapterList(sortedChapters);
  }, [chapters]);

  // Reset success message when changes are made
  useEffect(() => {
    if (hasChanges) {
      setShowSuccessMessage(false);
    }
  }, [hasChanges]);

  const getIconByChapterType = (type) => {
    switch (type) {
      case "VIDEO":
        return <PlayCircle size={20} className="text-indigo-600" />;
      case "QUIZ":
        return <HelpCircle size={20} className="text-purple-600" />;
      case "ARTICLE":
        return <FileText size={20} className="text-green-600" />;
      default:
        return <BookOpen size={20} className="text-gray-600" />;
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) {
      console.log('No destination found');
      return;
    }

    if (result.source.index === result.destination.index) {
      console.log('No change in position');
      return;
    }

    const items = Array.from(chapterList);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    console.log('Reordering from', result.source.index, 'to', result.destination.index);
    setChapterList(items);
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Create order map for API call
    const orderMap = {};
    chapterList.forEach((chapter, index) => {
      orderMap[chapter.id] = index + 1;
    });

    try {
      await dispatch(updateChapterOrder(orderMap));
      setHasChanges(false);
      setShowSuccessMessage(true);
      
      // Show success message briefly before closing
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
        onClose(); // Close the dialog
      }, 1500); // 1.5 second delay to show success message
    } catch (error) {
      console.error("Failed to update chapter order:", error);
      // Don't close dialog on error - let user see the error message
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <GripVertical size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Reorder Chapters</h2>
                <p className="text-white/80 text-sm">Drag and drop to change chapter order</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={!hasChanges || updateOrderLoading}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 disabled:bg-white/10 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2 font-medium"
              >
                {updateOrderLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Save Order
                  </>
                )}
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <XCircle size={24} className="text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <DragDropContext 
            onDragEnd={handleDragEnd}
            onDragStart={() => {
              // Optional: Add any drag start logic
            }}
          >
            <Droppable droppableId="chapters" type="chapter">
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={`transition-all duration-300 ${
                    snapshot.isDraggingOver 
                      ? "bg-indigo-50 rounded-lg border-2 border-dashed border-indigo-300" 
                      : "border-2 border-transparent"
                  }`}
                  style={{
                    minHeight: chapterList.length <= 2 ? '300px' : '200px',
                    padding: snapshot.isDraggingOver ? '16px' : '0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  {chapterList.map((chapter, index) => (
                    <Draggable 
                      key={chapter.id} 
                      draggableId={chapter.id.toString()} 
                      index={index}
                      isDragDisabled={updateOrderLoading}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`${
                            snapshot.isDragging
                              ? "shadow-2xl rotate-2 scale-105 z-50"
                              : "shadow-md hover:shadow-lg"
                          } transition-all duration-200 flex-shrink-0`}
                          style={{
                            ...provided.draggableProps.style,
                            transform: snapshot.isDragging 
                              ? provided.draggableProps.style?.transform 
                              : 'none',
                            marginBottom: '12px',
                          }}
                        >
                          <div
                            className={`bg-white rounded-xl border-2 p-4 flex items-center gap-4 ${
                              snapshot.isDragging
                                ? "border-indigo-300 bg-indigo-50"
                                : "border-gray-200 hover:border-indigo-300"
                            }`}
                          >
                            {/* Drag Handle */}
                            <div
                              {...provided.dragHandleProps}
                              className="p-2 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing hover:bg-gray-100 rounded-lg transition-colors"
                              title="Drag to reorder"
                            >
                              <GripVertical size={20} />
                            </div>

                            {/* Order Number */}
                            <div className="w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-semibold text-sm">
                              {index + 1}
                            </div>

                            {/* Chapter Icon */}
                            <div className="p-2 bg-gray-100 rounded-lg">
                              {getIconByChapterType(chapter.type)}
                            </div>

                            {/* Chapter Details */}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-gray-800 truncate">
                                {chapter.title}
                              </h3>
                              <p className="text-sm text-gray-500 truncate">
                                {chapter.description}
                              </p>
                            </div>

                            {/* Chapter Type Badge */}
                            <div className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                              {chapter.type}
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {/* Extra space for better drop experience with few items */}
                  {chapterList.length <= 2 && (
                    <div 
                      className="flex-1 min-h-[100px] flex items-center justify-center text-gray-400 text-sm"
                      style={{ minHeight: '100px' }}
                    >
                      <div className="text-center">
                        <GripVertical size={32} className="mx-auto mb-2 opacity-30" />
                        <p className="opacity-60">Drop chapters here to reorder</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {/* Instructions */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <div className="p-1 bg-blue-100 rounded">
                <BookOpen size={16} className="text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-blue-800 mb-1">How to reorder chapters:</h4>
                <p className="text-sm text-blue-700">
                  Click and drag the grip handle (⋮⋮) to move chapters up or down. 
                  Click "Save Order" to save your changes to the server.
                </p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {updateOrderError && (
            <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2">
                <XCircle size={20} className="text-red-600" />
                <p className="text-red-700 font-medium">Failed to update chapter order</p>
              </div>
              <p className="text-red-600 text-sm mt-1">{updateOrderError}</p>
            </div>
          )}

          {/* Success Message */}
          {showSuccessMessage && !updateOrderError && (
            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2">
                <CheckCircle size={20} className="text-green-600" />
                <p className="text-green-700 font-medium">Chapter order saved successfully! Closing dialog...</p>
              </div>
            </div>
          )}

          {/* Changes Pending Message */}
          {hasChanges && !showSuccessMessage && (
            <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-yellow-700 font-medium">You have unsaved changes. Click "Save Order" to save them.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReOrderChapter;
